import { useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth, differenceInCalendarDays } from 'date-fns';
import { visitClientName, visitCaregiverName, visitActualHours } from '../../lib/axiscare';

// AxisCare-style month calendar. One table per week; rows are clients,
// caregivers or service lines; columns are the seven days with the day's
// scheduled hours in the header; a trailing column carries the week's
// S (scheduled) and B (billable, clocked) hour totals. Chips are colored by
// status with the same palette AxisCare uses, so the office reads it the
// same way they read AxisCare today.

export const STATUS_STYLE = {
  Unassigned:  { bg: 'var(--danger)',      border: 'var(--danger)',         color: 'var(--white)', label: 'Unassigned' },
  Verified:    { bg: 'var(--success)',     border: 'var(--success)',        color: 'var(--white)', label: 'Verified' },
  Completed:   { bg: 'var(--info-bg)',     border: 'var(--info-border)',    color: 'var(--text)',  label: 'Completed' },
  'In-Progress': { bg: 'var(--success-bg)', border: 'var(--success-border)', color: 'var(--text)', label: 'In-Progress' },
  Scheduled:   { bg: 'var(--accent-bg)',   border: 'var(--accent-border)',  color: 'var(--text)',  label: 'Scheduled' },
  Standalone:  { bg: 'var(--copper-bg)',   border: 'var(--copper-border)',  color: 'var(--text)',  label: 'Standalone' },
  Missed:      { bg: 'var(--warn-bg)',     border: 'var(--warn)',           color: 'var(--text)',  label: 'Missed' },
  Removed:     { bg: 'var(--bg3)',         border: 'var(--border2)',        color: 'var(--text3)', label: 'Removed' },
};
export const LEGEND_ORDER = ['Unassigned', 'Verified', 'Completed', 'In-Progress', 'Scheduled', 'Standalone', 'Missed', 'Removed'];

// Display status for a normalized visit (see useVisits).
export const chipStatus = (v) => {
  if (v._status === 'Missed') return 'Missed';
  if (v._status === 'Unassigned') return 'Unassigned';
  if (v.verified) return 'Verified';
  if (v.clockIn && v.clockOut) return 'Completed';
  if (v.clockIn && !v.clockOut) return 'In-Progress';
  if (v._standalone) return 'Standalone';
  return 'Scheduled';
};

const startOf = (v) => v.scheduledStartDate || v.startDate;
const endOf = (v) => v.scheduledEndDate || v.endDate;
const shortTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return format(d, 'h:mm').replace(/:00$/, '') + (d.getHours() < 12 ? 'a' : 'p');
};
const spansMidnight = (v) => {
  const s = new Date(startOf(v)), e = new Date(endOf(v));
  return !isNaN(s) && !isNaN(e) && differenceInCalendarDays(e, s) > 0;
};
const serviceLabel = (v) => v.service?.description || v.service?.name || v.service?.code || 'Service';
const serviceCode = (v) => v.service?.code || '';
const lastFirst = (o) => o ? [o.lastName, o.firstName].filter(Boolean).join(', ') : '';

export function Legend() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 120px))', gap: 6 }}>
      {LEGEND_ORDER.map(k => {
        const s = STATUS_STYLE[k];
        return <div key={k} style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 500, textAlign: 'center' }}>{s.label}</div>;
      })}
    </div>
  );
}

export default function MonthGrid({ visits, month, groupBy = 'client', onSelect, selected }) {
  const weeks = useMemo(() => {
    const first = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const last = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    const out = [];
    for (let d = first; d <= last; d = addDays(d, 7)) out.push(Array.from({ length: 7 }, (_, i) => addDays(d, i)));
    return out;
  }, [month]);

  const rowKey = (v) => {
    if (groupBy === 'caregiver') return v.caregiver ? lastFirst(v.caregiver) : 'Unassigned';
    if (groupBy === 'service') return `${serviceLabel(v)}|${serviceCode(v)}`;
    return lastFirst(v.client) || visitClientName(v);
  };
  const rowTitle = (key) => groupBy === 'service' ? key.split('|') : [key];

  const byDay = useMemo(() => {
    const m = new Map();
    visits.forEach(v => {
      const d = new Date(startOf(v));
      if (isNaN(d)) return;
      const k = format(d, 'yyyy-MM-dd');
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(v);
    });
    m.forEach(list => list.sort((a, b) => String(startOf(a)).localeCompare(String(startOf(b)))));
    return m;
  }, [visits]);

  const dayVisits = (day) => byDay.get(format(day, 'yyyy-MM-dd')) || [];
  const hoursOf = (list) => list.filter(v => v._status !== 'Missed').reduce((s, v) => s + (v._hours || 0), 0);
  const billableOf = (list) => list.filter(v => v.clockIn && v._status !== 'Missed').reduce((s, v) => s + visitActualHours(v), 0);
  const fmtH = (h) => `${(Math.round(h * 100) / 100).toString()}h`;

  const th = { fontSize: 11, fontWeight: 600, color: 'var(--text2)', padding: '6px 8px', textAlign: 'left', background: 'var(--bg2)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: 4, verticalAlign: 'top', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', minWidth: 118 };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {weeks.map((days, wi) => {
        const weekVisits = days.flatMap(dayVisits);
        const keys = [...new Set(weekVisits.map(rowKey))].sort((a, b) => a.localeCompare(b));
        const weekS = hoursOf(weekVisits), weekB = billableOf(weekVisits);
        return (
          <div key={wi} style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--card-bg)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 900 }}>
              <colgroup>
                <col style={{ width: 140 }} />{days.map((d, i) => <col key={i} />)}<col style={{ width: 96 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={th}>{groupBy === 'service' ? 'Services' : groupBy === 'caregiver' ? 'Caregivers' : 'Clients'}</th>
                  {days.map(d => {
                    const inMonth = isSameMonth(d, month), today = isSameDay(d, new Date());
                    return (
                      <th key={d.toISOString()} style={{ ...th, color: today ? 'var(--accent)' : inMonth ? 'var(--text2)' : 'var(--text3)', background: today ? 'var(--accent-bg)' : 'var(--bg2)' }}>
                        {format(d, 'EEE d')} <span style={{ fontWeight: 400, fontSize: 10 }}>({fmtH(hoursOf(dayVisits(d)))})</span>
                      </th>
                    );
                  })}
                  <th style={{ ...th, borderRight: 'none' }} title="S = scheduled hours · B = billable (clocked) hours">Week</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 && (
                  <tr><td style={{ ...td, color: 'var(--text3)', fontSize: 11 }} colSpan={9}>No visits this week</td></tr>
                )}
                {keys.map((key, ri) => {
                  const [title, sub] = rowTitle(key);
                  const rowVisits = weekVisits.filter(v => rowKey(v) === key);
                  return (
                    <tr key={key}>
                      <td style={{ ...td, background: 'var(--bg2)', fontSize: 12 }}>
                        <div style={{ fontWeight: 500, color: 'var(--text)', lineHeight: 1.3 }}>{title}</div>
                        {sub && <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>{sub}</div>}
                      </td>
                      {days.map(d => {
                        const inMonth = isSameMonth(d, month);
                        const cell = rowVisits.filter(v => isSameDay(new Date(startOf(v)), d));
                        return (
                          <td key={d.toISOString()} style={{ ...td, background: inMonth ? 'transparent' : 'var(--bg2)' }}>
                            {cell.map((v, i) => {
                              const st = chipStatus(v), s = STATUS_STYLE[st];
                              const isSel = selected && (selected.id ?? selected.visitId) === (v.id ?? v.visitId);
                              const who = groupBy === 'caregiver' ? lastFirst(v.client) : (v.caregiver ? `${v.caregiver.firstName || ''} ${v.caregiver.lastName || ''}`.trim() : 'Unassigned');
                              return (
                                <div key={i} onClick={() => onSelect && onSelect(isSel ? null : v)} title={`${visitClientName(v)} · ${visitCaregiverName(v)} · ${st}`}
                                  style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 4, padding: '4px 6px', marginBottom: 3, fontSize: 10.5, lineHeight: 1.3, cursor: onSelect ? 'pointer' : 'default', outline: isSel ? '2px solid var(--accent)' : 'none' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{shortTime(startOf(v))} – {shortTime(endOf(v))}</span>
                                    <span style={{ fontWeight: 500 }}>{fmtH(v._hours || 0)}</span>
                                  </div>
                                  {groupBy !== 'service' && <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, opacity: 0.85 }}>{serviceCode(v)}</div>}
                                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who}</div>
                                  {spansMidnight(v) && <div style={{ fontSize: 9.5, opacity: 0.85 }}>⤳ Spans midnight</div>}
                                </div>
                              );
                            })}
                          </td>
                        );
                      })}
                      {ri === 0 && (
                        <td rowSpan={keys.length} style={{ ...td, borderRight: 'none', background: 'var(--bg2)', fontSize: 11, verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <div><b>S</b> {fmtH(weekS)}</div>
                          <div style={{ marginTop: 4 }}><b>B</b> {fmtH(weekB)}</div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
